<script setup lang="ts">
import { ref, computed } from 'vue'
import router from '@/router'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useUserInfoStore } from '@/stores/modules/user'

const route = useRoute()
const defaultActive = ref(route.path)

// 管理员才能看到 IC命名、打印设计器、送货单管理
const userStore = useUserInfoStore()
const isAdmin = computed(() => {
  const role = userStore.user?.roleCode
  return role === 'admin' || role === 'superadmin'
})

onBeforeRouteUpdate((to) => {
  defaultActive.value = to.path
})

const handleSelect = (path: string) => {
  emit('select', path)
  router.push(path)
}

const emit = defineEmits<{
  (e: 'select', path: string): void
}>()
</script>

<template>
  <el-menu
    :default-active="defaultActive"
    @select="handleSelect"
    :unique-opened="true"
    class="side-menu"
    :collapse="false"
  >
    <!-- 客户中心首页 -->
    <el-menu-item index="/customer/home">
      <el-icon><HomeFilled /></el-icon>
      <span>客户中心首页</span>
    </el-menu-item>

    <!-- 项目中心 -->
    <el-sub-menu index="/customer/project">
      <template #title>
        <el-icon><FolderOpened /></el-icon>
        <span>项目中心</span>
      </template>
      <el-menu-item index="/customer/project/create">
        <el-icon><CirclePlus /></el-icon>
        <span>新建项目</span>
      </el-menu-item>
      <el-menu-item index="/customer/project/active">
        <el-icon><Loading /></el-icon>
        <span>进行中的项目</span>
      </el-menu-item>
      <el-menu-item index="/customer/project/completed">
        <el-icon><CircleCheck /></el-icon>
        <span>已完工项目</span>
      </el-menu-item>
    </el-sub-menu>

    <!-- 生产中心 -->
    <el-sub-menu index="/customer/production">
      <template #title>
        <el-icon><Box /></el-icon>
        <span>生产中心</span>
      </template>
      <el-menu-item index="/customer/production/order">
        <el-icon><Document /></el-icon>
        <span>生产订单</span>
      </el-menu-item>
      <el-menu-item index="/customer/production/progress">
        <el-icon><DataLine /></el-icon>
        <span>生产进度</span>
      </el-menu-item>
    </el-sub-menu>

    <!-- 用户中心 -->
    <el-sub-menu index="/customer/usercenter">
      <template #title>
        <el-icon><User /></el-icon>
        <span>用户中心</span>
      </template>
      <el-menu-item index="/customer/usercenter/account">
        <el-icon><UserFilled /></el-icon>
        <span>账号信息</span>
      </el-menu-item>
      <el-menu-item index="/customer/usercenter/file">
        <el-icon><Folder /></el-icon>
        <span>文件管理</span>
      </el-menu-item>
      <el-menu-item index="/customer/usercenter/notification">
        <el-icon><Bell /></el-icon>
        <span>消息通知</span>
      </el-menu-item>
      <el-menu-item index="/customer/usercenter/security">
        <el-icon><Lock /></el-icon>
        <span>安全中心</span>
      </el-menu-item>
      <el-menu-item index="/customer/usercenter/address">
        <el-icon><Location /></el-icon>
        <span>地址管理</span>
      </el-menu-item>
      <el-menu-item index="/customer/usercenter/qualification">
        <el-icon><Stamp /></el-icon>
        <span>资质信息</span>
      </el-menu-item>
    </el-sub-menu>

    <!-- 工具箱 -->
    <el-sub-menu index="/customer/tools">
      <template #title>
        <el-icon><SetUp /></el-icon>
        <span>工具箱</span>
      </template>
      <el-menu-item v-if="isAdmin" index="/customer/tools/icname">
        <el-icon><Cpu /></el-icon>
        <span>IC命名工具</span>
      </el-menu-item>
      <el-menu-item index="/customer/tools/mqtt-test">
        <el-icon><Connection /></el-icon>
        <span>MQTT通讯测试</span>
      </el-menu-item>
      <el-menu-item index="/customer/tools/mqtt-upgrade">
        <el-icon><Upload /></el-icon>
        <span>MQTT升级工具</span>
      </el-menu-item>
      <el-menu-item index="/customer/tools/serial-debug">
        <el-icon><Connection /></el-icon>
        <span>串口调试工具</span>
      </el-menu-item>
      <el-menu-item v-if="isAdmin" index="/customer/tools/print-designer">
        <el-icon><Printer /></el-icon>
        <span>打印设计器</span>
      </el-menu-item>
      <el-menu-item v-if="isAdmin" index="/customer/tools/delivery-order">
        <el-icon><Document /></el-icon>
        <span>送货单管理</span>
      </el-menu-item>
    </el-sub-menu>
  </el-menu>
</template>

<style scoped lang="scss">
.side-menu:not(.el-menu--collapse) {
  @apply w-[200px];
}
.side-menu {
  @apply grow overflow-x-hidden;
}
</style>
